<template>
  <section class="pricing-section">
    <div class="pricing-content">
      <p class="solidarity-subtitle">{{ t('pricing.solidarity_subtitle') }}</p>

      <h2 class="solidarity-heading">
        <span>{{ t('pricing.solidarity_heading_pre') }}</span>
        <span class="highlight">{{ t('pricing.solidarity_heading_highlight') }}</span>
        <span>{{ t('pricing.solidarity_heading_post') }}</span>
      </h2>

      <p class="seats-text">
        <span>{{ t('pricing.seats_label') }}</span>
        <span class="highlight">{{ t('pricing.seats_count') }}</span>
        <span>{{ t('pricing.seats_only') }}</span>
      </p>

      <!-- Period selector -->
      <div class="period-selector" @click="dropdownOpen = !dropdownOpen" :class="{ open: dropdownOpen }">
        <span class="period-label">{{ currentPeriod.label }}</span>
        <svg class="chevron" width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <div v-if="dropdownOpen" class="dropdown-menu" @click.stop>
          <button
            v-for="period in periods"
            :key="period.key"
            class="dropdown-item"
            :class="{ active: period.key === currentPeriod.key }"
            @click="selectPeriod(period)"
          >
            {{ period.label }}
          </button>
        </div>
      </div>

      <!-- Pricing cards -->
      <div class="pricing-cards">
        <!-- Immersion -->
        <div class="pricing-column">
          <div class="pricing-card">
            <div class="card-header">
              <span class="chip">{{ t('pricing.card1_chip') }}</span>
              <span class="plan-name">{{ t('pricing.card1_name') }}</span>
            </div>
            <div class="card-body">
              <div class="price-display">
                <span v-if="loading" class="price-loading">…</span>
                <template v-else>
                  <span class="price-amount">{{ formatPrice(currentPrices.immersion) }}</span>
                  <span class="price-currency">{{ currencySign }}</span>
                </template>
              </div>
            </div>
          </div>
          <button class="cta-btn" @click="handleSubscribe">{{ t('pricing.subscribe_immersion') }}</button>
        </div>

        <!-- Immersion Plus -->
        <div class="pricing-column">
          <div class="pricing-card">
            <div class="card-header">
              <span class="plan-name">{{ t('pricing.card2_name') }}</span>
              <span class="chip">{{ t('pricing.card2_chip') }}</span>
            </div>
            <div class="card-body">
              <div class="price-display">
                <span v-if="loading" class="price-loading">…</span>
                <template v-else>
                  <span class="price-amount">{{ formatPrice(currentPrices.immersionPlus) }}</span>
                  <span class="price-currency">{{ currencySign }}</span>
                </template>
              </div>
            </div>
          </div>
          <button class="cta-btn" @click="handleSubscribe">{{ t('pricing.subscribe_immersion_plus') }}</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, defineEmits, onMounted } from 'vue'
import { useLanguage } from '@/composables/useLanguage'
import { usePackages } from '@/composables/usePackages'

const { t } = useLanguage()
const { findPackage, loadPackages, loading } = usePackages()

const emit = defineEmits(['ctaClick'])

const periods = computed(() => [
  { key: 3, label: t('pricing.period_3') },
  { key: 6, label: t('pricing.period_6') },
  { key: 12, label: t('pricing.period_12') },
])

const dropdownOpen = ref(false)
const currentPeriodKey = ref(3)

const currentPeriod = computed(
  () => periods.value.find((p) => p.key === currentPeriodKey.value) || periods.value[0]
)

const currentPrices = computed(() => {
  const immersion = findPackage('immersion', currentPeriodKey.value)
  const immersionPlus = findPackage('immersion plus', currentPeriodKey.value)
  return {
    immersion: immersion?.totalPrice ?? null,
    immersionPlus: immersionPlus?.totalPrice ?? null,
  }
})

const currencySign = computed(() => {
  const pkg =
    findPackage('immersion', currentPeriodKey.value) ||
    findPackage('immersion plus', currentPeriodKey.value)
  return pkg?.currencySign ?? '$'
})

const formatPrice = (value) => {
  if (value == null) return '—'
  return Number.isInteger(value) ? value.toString() : value.toFixed(0)
}

const selectPeriod = (period) => {
  currentPeriodKey.value = period.key
  dropdownOpen.value = false
}

const handleSubscribe = () => {
  emit('ctaClick')
}

onMounted(() => {
  loadPackages('kids').catch((err) => {
    console.error('Failed to load kids packages:', err)
  })
})
</script>

<style scoped>
.pricing-section {
  width: 100%;
  position: relative;
  background: var(--Color-6, #082C40);
  padding: clamp(40px, 6vw, 80px) clamp(20px, 4vw, 40px);
  overflow: hidden;
  box-sizing: border-box;
}

.pricing-content {
  position: relative;
  z-index: 1;
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(16px, 2vw, 24px);
}

.solidarity-subtitle {
  margin: 0;
  font-size: clamp(14px, 1.2vw, 18px);
  color: rgba(255, 255, 255, 0.85);
  text-align: center;
}

.solidarity-heading {
  margin: 0;
  font-size: clamp(28px, 3.4vw, 48px);
  font-weight: 700;
  color: #fff;
  text-align: center;
  line-height: 1.2;
}

.solidarity-heading .highlight {
  color: #F47D21;
}

.seats-text {
  margin: 0;
  font-size: clamp(15px, 1.4vw, 20px);
  color: #fff;
  text-align: center;
}

.seats-text .highlight {
  color: #F47D21;
  font-weight: 700;
}

/* Period Selector */
.period-selector {
  position: relative;
  z-index: 10;
  width: 100%;
  margin-top: clamp(8px, 1.5vw, 16px);
  background: rgba(255, 255, 255, 0.1);
  border: 0.95px solid #27B4FF;
  border-radius: 999px;
  box-shadow: 0px 2.34px 21.25px 0px #B1E4FF inset;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  padding: clamp(14px, 1.5vw, 20px) clamp(20px, 3vw, 32px);
  color: #fff;
  font-size: clamp(16px, 1.4vw, 20px);
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
  transition: background 0.2s ease;
}

.period-selector:hover {
  background: rgba(255, 255, 255, 0.15);
}

.period-label {
  flex: 1;
  text-align: center;
}

.chevron {
  position: absolute;
  inset-inline-end: clamp(20px, 3vw, 32px);
  transition: transform 0.25s ease;
  color: #fff;
}

.period-selector.open .chevron {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: #0d3a52;
  border: 1px solid #27B4FF;
  border-radius: 16px;
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  z-index: 5;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

.dropdown-item {
  background: transparent;
  border: none;
  color: #fff;
  font-size: clamp(15px, 1.3vw, 18px);
  font-weight: 600;
  padding: 12px 16px;
  border-radius: 10px;
  cursor: pointer;
  text-align: center;
  font-family: inherit;
  transition: background 0.2s ease;
}

.dropdown-item:hover {
  background: rgba(39, 180, 255, 0.2);
}

.dropdown-item.active {
  background: rgba(39, 180, 255, 0.3);
}

/* Pricing Cards */
.pricing-cards {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: clamp(16px, 2vw, 24px);
  margin-top: clamp(8px, 1.5vw, 16px);
}

.pricing-column {
  display: flex;
  flex-direction: column;
  gap: clamp(12px, 1.5vw, 18px);
}

.pricing-card {
  background: rgba(255, 255, 255, 0.1);
  border: 0.95px solid #27B4FF;
  border-radius: 24px;
  padding: 0;
  display: flex;
  flex-direction: column;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0px 2.34px 21.25px 0px #B1E4FF inset;
}

/* Card Header */
.card-header {
  background-image: url('/package-header.png');
  background-size: cover;
  background-position: center right;
  background-repeat: no-repeat;
  background-color: #0e6fa3;
  border-radius: 16px;
  padding: clamp(12px, 1.4vw, 20px) clamp(20px, 2.5vw, 32px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: clamp(8px, 1vw, 14px);
  position: relative;
  overflow: hidden;
}

.chip {
  display: block;
  width: 60%;
  background: #F47D21;
  color: #fff;
  font-weight: 700;
  font-size: clamp(20px, 2.4vw, 32px);
  padding: 0 clamp(8px, 1vw, 14px);
  border-radius: 999px;
  white-space: nowrap;
  text-align: center;
  letter-spacing: 0.4em;
}

.plan-name {
  color: #fff;
  font-weight: 700;
  font-size: clamp(24px, 3vw, 40px);
  white-space: nowrap;
  line-height: 1;
}

/* Card Body */
.card-body {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(28px, 3.5vw, 48px) clamp(16px, 2vw, 24px);
  flex: 1;
}

.price-display {
  display: flex;
  align-items: baseline;
  gap: clamp(6px, 1vw, 10px);
}

.price-amount {
  font-size: clamp(48px, 6vw, 80px);
  font-weight: 700;
  color: #fff;
  line-height: 1;
  font-family: 'Poppins', sans-serif;
}

.price-currency {
  font-size: clamp(28px, 3.5vw, 48px);
  font-weight: 700;
  color: #F47D21;
  line-height: 1;
}

.price-loading {
  font-size: clamp(48px, 6vw, 80px);
  font-weight: 700;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1;
  font-family: 'Poppins', sans-serif;
}

/* CTA */
.cta-btn {
  width: 100%;
  background: #F47D21;
  border: none;
  border-radius: 999px;
  padding: clamp(12px, 1.5vw, 16px) clamp(16px, 2vw, 24px);
  color: #fff;
  font-size: clamp(14px, 1.3vw, 18px);
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.2s ease, transform 0.2s ease;
}

.cta-btn:hover {
  background: #e56a10;
  transform: translateY(-2px);
}

/* Responsive */
@media (max-width: 768px) {
  .pricing-cards {
    grid-template-columns: 1fr;
    max-width: 420px;
    margin-left: auto;
    margin-right: auto;
  }

  .card-header {
    flex-direction: column;
    gap: 8px;
  }
}

@media (max-width: 480px) {
  .pricing-section {
    padding: 32px 16px;
  }

  .price-amount {
    font-size: 56px;
  }

  .price-currency {
    font-size: 32px;
  }
}
</style>
